import * as React from 'react';
import * as moment from 'moment';
import {Alert, Button, Form, Modal} from 'antd';
import {FormComponentProps} from 'antd/lib/form';

import {
  IncomingInvoice,
  InventoryTransportLetter,
  Partner,
  SaveInventoryTransportLetterRequest
} from '../api/dtos/dtos';
import locales from '../locales';
import InputFormItem from '../common/form-items/InputFormItem';
import DatePickerFormItem from '../common/form-items/DatePickerFormItem';
import SubmitCancelFormItem from '../common/form-items/SubmitCancelFormItem';
import {formatDate, toIsoString, toMoment} from '../utils/date-utils';
import AutoCompleteFormItem from '../common/form-items/AutoCompleteFormItem';
import PartnerAutoCompleteFormItem from "../incoming-invoices/details/PartnerAutoCompleteFormItem";
import HiddenFormItem from "../common/form-items/HiddenFormItem";
import {Link} from "react-router-dom";
import * as paths from "../paths";
import {groupNumber, withCurrency} from "../utils/number-utils";
import IncomingInvoiceSelector from "../inventory-stock-details/IncomingInvoiceSelector";

interface Props extends FormComponentProps {
  editable: boolean;
  transportLetter?: InventoryTransportLetter;
  partners: Partner[];
  users?: string[];
  onPartnerSearch: (value: string) => void;
  onPartnerDelete: (id: number) => void;
  onFormSubmit?: (transportLetter: SaveInventoryTransportLetterRequest) => void;
  onFormCancel: () => void;
}

interface State {
  selectorVisible: boolean;
  selectedInvoice?: IncomingInvoice;
}

const FIELD_TRANSPORT_LETTER_NUMBER = 'transportLetterNumber';
const FIELD_DATE = 'date';
const FIELD_INCOMING_INVOICE = 'incomingInvoiceId';
const FIELD_PARTNER_NAME = 'partnerName';
const FIELD_RESPONSIBLE_USER = 'responsibleUser';

class InventoryTransportLetterForm extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      selectorVisible: false,
      selectedInvoice: props.transportLetter?.incomingInvoice,
    };
  }

  public render() {
    const {
      form,
      editable,
      transportLetter,
      partners,
      users,
      onPartnerSearch,
      onPartnerDelete,
    } = this.props;

    const {selectorVisible, selectedInvoice} = this.state;
    const disabled = !editable;

    return (
      <Form>
        <InputFormItem
          form={form}
          fieldId={FIELD_TRANSPORT_LETTER_NUMBER}
          label={locales.id}
          initialValue={transportLetter ? transportLetter.transportLetterNumber : null}
          required={true}
          disabled={disabled}
        />

        <DatePickerFormItem
          form={form}
          fieldId={FIELD_DATE}
          label={locales.date}
          initialValue={this.getInitialDateValue(transportLetter)}
          required={true}
          disabled={disabled}
          showTime={true}
        />

        <HiddenFormItem
          form={form}
          fieldId={FIELD_INCOMING_INVOICE}
          label={locales.incoming_invoice}
          initialValue={selectedInvoice ? selectedInvoice.id : undefined}
          required={false}
          disabled={disabled}
        >
          {!selectedInvoice &&
              <Button icon="file-search" disabled={disabled}
                      onClick={this.showSelector}>{locales.select_incoming_invoice}</Button>
          }
          {selectedInvoice && this.renderSelectedInvoiceAlert(selectedInvoice)}
        </HiddenFormItem>

        <PartnerAutoCompleteFormItem
          form={form}
          fieldId={FIELD_PARTNER_NAME}
          label={locales.company_name}
          initialValue={transportLetter ? transportLetter.partner.name : null}
          required={true}
          disabled={disabled}
          partners={partners}
          onSearch={onPartnerSearch}
          onDelete={onPartnerDelete}
        />

        <AutoCompleteFormItem
          form={form}
          fieldId={FIELD_RESPONSIBLE_USER}
          label={locales.responsible_user}
          initialValue={transportLetter ? transportLetter.responsibleUser : null}
          required={false}
          validateOnDataSource={true}
          disabled={disabled}
          dataSource={users || []}
        />

        {editable && (
          <SubmitCancelFormItem
            disabled={disabled}
            onSubmit={this.onFormSubmit}
            onCancel={this.onFormCancel}
          />
        )}

        <Modal
          title={locales.select_incoming_invoice}
          visible={selectorVisible}
          footer={null}
          onCancel={this.hideSelector}
          width="auto"
          style={{margin: '0 24px'}}
          destroyOnClose={true}
        >
          <IncomingInvoiceSelector onSelect={this.onInvoiceSelected}/>
        </Modal>
      </Form>
    );
  }

  private renderSelectedInvoiceAlert = (invoice: IncomingInvoice): React.ReactNode => {
    const content = (
      <table>
        <tbody>
        {this.alertRow(locales.invoice_number, <Link
          to={paths.viewIncomingInvoicePath(invoice.id)}>{invoice.invoiceNumber}</Link>)}
        {this.alertRow(locales.appellation, invoice.name)}
        {this.alertRow(locales.company_name, invoice.companyName)}
        {this.alertRow(locales.issue_date, formatDate(invoice.issueDate))}
        {this.alertRow(locales.sum, withCurrency(groupNumber(invoice.sum)))}
        </tbody>
      </table>
    );

    return (
      <Alert
        style={{marginBottom: 16}}
        type="info"
        message={content}
        closable={this.props.editable}
        onClose={this.onInvoiceCleared}
      />
    );
  };

  private alertRow = (title: string, value?: React.ReactNode) => {
    if (!value) {
      return null;
    }
    return (
      <tr>
        <td style={{textAlign: 'right', paddingRight: 8}}><b>{title}</b></td>
        <td>{value}</td>
      </tr>
    );
  };

  private showSelector = () => {
    this.setState({selectorVisible: true});
  };

  private hideSelector = () => {
    this.setState({selectorVisible: false});
  };

  private onInvoiceSelected = (invoice: IncomingInvoice) => {
    this.setState({
      selectedInvoice: invoice,
      selectorVisible: false,
    }, () => {
      this.props.form.setFieldsValue({[FIELD_INCOMING_INVOICE]: invoice.id});
      this.props.form.validateFields([FIELD_INCOMING_INVOICE], {force: true}, () => undefined);
    });
  };

  private onInvoiceCleared = () => {
    this.setState({selectedInvoice: undefined}, () => {
      this.props.form.setFieldsValue({[FIELD_INCOMING_INVOICE]: undefined});
      this.props.form.validateFields([FIELD_INCOMING_INVOICE], {force: true}, () => undefined);
    });
  };

  private getInitialDateValue = (transportLetter?: InventoryTransportLetter): moment.Moment | undefined => {
    return transportLetter && transportLetter.date ? toMoment(transportLetter.date) : undefined;
  };

  private onFormCancel = () => {
    this.props.form.resetFields();
    this.props.onFormCancel();
  };

  private onFormSubmit = () => {
    const {transportLetter, form, onFormSubmit} = this.props;
    form.validateFieldsAndScroll((err, values) => {
      if (!err && onFormSubmit) {
        const dateValue = values[FIELD_DATE] ? toIsoString(values[FIELD_DATE]) : '';
        onFormSubmit({
          ...transportLetter,
          transportLetterNumber: values[FIELD_TRANSPORT_LETTER_NUMBER],
          date: dateValue,
          incomingInvoiceId: values[FIELD_INCOMING_INVOICE],
          partnerName: values[FIELD_PARTNER_NAME],
          responsibleUser: values[FIELD_RESPONSIBLE_USER],
        });
      }
    });
  };
}

export default Form.create<Props>()(InventoryTransportLetterForm);




// WEBPACK FOOTER //
// ./src/inventory-transport-letters/InventoryTransportLetterForm.tsx